import { Injectable } from '@angular/core';
import API, { graphqlOperation } from '@aws-amplify/api-graphql';
import { BehaviorSubject } from 'rxjs';
import { Employee } from './employee-data.service';

export interface PayrollItem {
  employeeId: string;
  employeeName: string;
  jmbg?: string;
  grossSalary: number;
  netSalary: number;
  tax: number;
  healthInsurance: number;
  pensionInsurance: number;
  unemploymentInsurance: number;
  totalContributions: number;
}

export interface Payroll {
  id: string;
  month: number;
  year: number;
  status: 'DRAFT' | 'CONFIRMED';
  items: PayrollItem[];
  totalGross: number;
  totalNet: number;
  totalTax: number;
  totalContributions: number;
  contributionPaymentDate?: string;
  workingDays?: number;
  rates?: any;
  clientID: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreatePayrollInput {
  month: number;
  year: number;
  items: PayrollItem[];
  totalGross: number;
  totalNet: number;
  totalTax: number;
  totalContributions: number;
  contributionPaymentDate?: string;
  workingDays?: number;
  rates?: any;
  clientID: string;
}

export interface UpdatePayrollInput {
  id: string;
  month?: number;
  year?: number;
  status?: 'DRAFT' | 'CONFIRMED';
  items?: PayrollItem[];
  totalGross?: number;
  totalNet?: number;
  totalTax?: number;
  totalContributions?: number;
  contributionPaymentDate?: string;
  workingDays?: number;
  rates?: any;
}

// Bosnia and Herzegovina (FBiH) rates
export const TAX_RATES = {
  INCOME_TAX: 10, // 10% income tax (on taxBase)
  PERSONAL_DEDUCTION: 300, // 300 KM personal deduction
  // Employee contributions (IZ plate)
  PIO_EMPLOYEE: 17, // 17% PIO IZ
  HEALTH_EMPLOYEE: 12.5, // 12.5% Zdravstvo IZ
  UNEMPLOYMENT_EMPLOYEE: 1.5, // 1.5% Nezaposlenost IZ
  // Employer contributions (NA platu)
  PIO_EMPLOYER: 2.5, // 2.5% PIO NA
  HEALTH_EMPLOYER: 2, // 2% Zdravstvo NA
  UNEMPLOYMENT_EMPLOYER: 0.5, // 0.5% Nezaposlenost NA
};

@Injectable({
  providedIn: 'root'
})
export class PayrollDataService {
  private payrollsSubject = new BehaviorSubject<Payroll[]>([]);
  public payrolls$ = this.payrollsSubject.asObservable();
  
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor() { }

  /**
   * List all payrolls for a client
   */
  async listPayrolls(clientID: string, nextToken?: string): Promise<{ items: Payroll[]; nextToken?: string }> {
    const statement = `
      query ListPayrollsByClientID($clientID: ID!, $limit: Int, $nextToken: String) {
        listPayrollsByClientID(clientID: $clientID, limit: $limit, nextToken: $nextToken) {
          items {
            id
            month
            year
            status
            items
            totalGross
            totalNet
            totalTax
            totalContributions
            contributionPaymentDate
            workingDays
            rates
            clientID
            createdAt
            updatedAt
          }
          nextToken
        }
      }
    `;

    this.loadingSubject.next(true);

    try {
      const response = (await API.graphql(
        graphqlOperation(statement, { clientID, limit: 100, nextToken })
      )) as any;
      
      const result = response.data.listPayrollsByClientID || { items: [], nextToken: null };
      const payrolls = (result.items || []).map((p: any) => ({
        ...p,
        items: typeof p.items === 'string' ? JSON.parse(p.items) : p.items
      }));
      
      this.payrollsSubject.next(payrolls);
      this.loadingSubject.next(false);
      
      return { items: payrolls, nextToken: result.nextToken };
    } catch (error) {
      console.error('Error listing payrolls:', error);
      this.loadingSubject.next(false);
      throw error;
    }
  }

  /**
   * Get a single payroll by ID
   */
  async getPayroll(id: string): Promise<Payroll> {
    const statement = `
      query GetPayroll($id: ID!) {
        getPayroll(id: $id) {
          id
          month
          year
          status
          items
          totalGross
          totalNet
          totalTax
          totalContributions
          contributionPaymentDate
          workingDays
          rates
          clientID
          createdAt
          updatedAt
        }
      }
    `;

    try {
      const response = (await API.graphql(
        graphqlOperation(statement, { id })
      )) as any;
      
      const payroll = response.data.getPayroll;
      return {
        ...payroll,
        items: typeof payroll.items === 'string' ? JSON.parse(payroll.items) : payroll.items
      };
    } catch (error) {
      console.error('Error getting payroll:', error);
      throw error;
    }
  }

  /**
   * Create a new payroll
   */
  async createPayroll(input: CreatePayrollInput): Promise<Payroll> {
    const statement = `
      mutation CreatePayroll($input: CreatePayrollInput!) {
        createPayroll(input: $input) {
          id
          month
          year
          status
          items
          totalGross
          totalNet
          totalTax
          totalContributions
          contributionPaymentDate
          workingDays
          rates
          clientID
          createdAt
          updatedAt
        }
      }
    `;

    const payrollInput = {
      ...input,
      items: JSON.stringify(input.items),
      rates: input.rates ? JSON.stringify(input.rates) : undefined,
      status: 'DRAFT'
    };

    try {
      const response = (await API.graphql(
        graphqlOperation(statement, { input: payrollInput })
      )) as any;
      
      const newPayroll = response.data.createPayroll;
      const parsedPayroll = {
        ...newPayroll,
        items: typeof newPayroll.items === 'string' ? JSON.parse(newPayroll.items) : newPayroll.items
      };
      
      // Update local state
      const currentPayrolls = this.payrollsSubject.getValue();
      this.payrollsSubject.next([...currentPayrolls, parsedPayroll]);
      
      return parsedPayroll;
    } catch (error) {
      console.error('Error creating payroll:', error);
      throw error;
    }
  }

  /**
   * Update an existing payroll
   */
  async updatePayroll(input: UpdatePayrollInput): Promise<Payroll> {
    const statement = `
      mutation UpdatePayroll($input: UpdatePayrollInput!) {
        updatePayroll(input: $input) {
          id
          month
          year
          status
          items
          totalGross
          totalNet
          totalTax
          totalContributions
          contributionPaymentDate
          workingDays
          rates
          clientID
          createdAt
          updatedAt
        }
      }
    `;

    const payrollInput = {
      ...input,
      items: input.items ? JSON.stringify(input.items) : undefined,
      rates: input.rates ? JSON.stringify(input.rates) : undefined
    };

    try {
      const response = (await API.graphql(
        graphqlOperation(statement, { input: payrollInput })
      )) as any;
      
      const updatedPayroll = response.data.updatePayroll;
      const parsedPayroll = {
        ...updatedPayroll,
        items: typeof updatedPayroll.items === 'string' ? JSON.parse(updatedPayroll.items) : updatedPayroll.items
      };
      
      // Update local state
      const currentPayrolls = this.payrollsSubject.getValue();
      const updatedList = currentPayrolls.map(p => 
        p.id === parsedPayroll.id ? parsedPayroll : p
      );
      this.payrollsSubject.next(updatedList);
      
      return parsedPayroll;
    } catch (error) {
      console.error('Error updating payroll:', error);
      throw error;
    }
  }

  /**
   * Confirm a payroll (change status from DRAFT to CONFIRMED)
   */
  async confirmPayroll(id: string): Promise<Payroll> {
    return this.updatePayroll({ id, status: 'CONFIRMED' });
  }

  /**
   * Delete a payroll
   */
  async deletePayroll(id: string): Promise<void> {
    const statement = `
      mutation DeletePayroll($input: DeletePayrollInput!) {
        deletePayroll(input: $input) {
          id
        }
      }
    `;

    try {
      await API.graphql(
        graphqlOperation(statement, { input: { id } })
      );
      
      // Update local state
      const currentPayrolls = this.payrollsSubject.getValue();
      this.payrollsSubject.next(currentPayrolls.filter(p => p.id !== id));
    } catch (error) {
      console.error('Error deleting payroll:', error);
      throw error;
    }
  }

  /**
   * Calculate salary details for an employee (FBiH formula)
   */
  calculateSalaryDetails(grossSalary: number): {
    netSalary: number;
    tax: number;
    healthInsurance: number;
    pensionInsurance: number;
    unemploymentInsurance: number;
    totalContributions: number;
    pioEmployer: number;
    healthEmployer: number;
    unemploymentEmployer: number;
    totalEmployerContributions: number;
    income: number;
    taxBase: number;
    totalCost: number;
  } {
    // Employee contributions (IZ plate)
    const pensionInsurance = Math.round(grossSalary * (TAX_RATES.PIO_EMPLOYEE / 100) * 100) / 100;
    const healthInsurance = Math.round(grossSalary * (TAX_RATES.HEALTH_EMPLOYEE / 100) * 100) / 100;
    const unemploymentInsurance = Math.round(grossSalary * (TAX_RATES.UNEMPLOYMENT_EMPLOYEE / 100) * 100) / 100;
    const totalContributions = pensionInsurance + healthInsurance + unemploymentInsurance;

    // Income and tax
    const income = Math.round((grossSalary - totalContributions) * 100) / 100;
    const taxBase = Math.max(0, Math.round((income - TAX_RATES.PERSONAL_DEDUCTION) * 100) / 100);
    const tax = Math.round(taxBase * (TAX_RATES.INCOME_TAX / 100) * 100) / 100;
    const netSalary = Math.round((income - tax) * 100) / 100;

    // Employer contributions (NA platu)
    const pioEmployer = Math.round(grossSalary * (TAX_RATES.PIO_EMPLOYER / 100) * 100) / 100;
    const healthEmployer = Math.round(grossSalary * (TAX_RATES.HEALTH_EMPLOYER / 100) * 100) / 100;
    const unemploymentEmployer = Math.round(grossSalary * (TAX_RATES.UNEMPLOYMENT_EMPLOYER / 100) * 100) / 100;
    const totalEmployerContributions = pioEmployer + healthEmployer + unemploymentEmployer;

    const totalCost = Math.round((grossSalary + totalEmployerContributions + totalContributions) * 100) / 100;

    return {
      netSalary,
      tax,
      healthInsurance,
      pensionInsurance,
      unemploymentInsurance,
      totalContributions,
      pioEmployer,
      healthEmployer,
      unemploymentEmployer,
      totalEmployerContributions,
      income,
      taxBase,
      totalCost
    };
  }

  /**
   * Generate payroll items from selected employees
   */
  generatePayrollItems(employees: Employee[]): PayrollItem[] {
    return employees.map(emp => {
      const grossSalary = emp.grossSalary || 0;
      const details = this.calculateSalaryDetails(grossSalary);
      
      return {
        employeeId: emp.id,
        employeeName: `${emp.firstName} ${emp.lastName}`,
        jmbg: emp.jmbg,
        grossSalary,
        netSalary: details.netSalary,
        tax: details.tax,
        healthInsurance: details.healthInsurance,
        pensionInsurance: details.pensionInsurance,
        unemploymentInsurance: details.unemploymentInsurance,
        totalContributions: details.totalContributions
      };
    });
  }

  /**
   * Calculate totals from payroll items
   */
  calculatePayrollTotals(items: PayrollItem[]): {
    totalGross: number;
    totalNet: number;
    totalTax: number;
    totalContributions: number;
  } {
    return {
      totalGross: Math.round(items.reduce((sum, item) => sum + item.grossSalary, 0) * 100) / 100,
      totalNet: Math.round(items.reduce((sum, item) => sum + item.netSalary, 0) * 100) / 100,
      totalTax: Math.round(items.reduce((sum, item) => sum + item.tax, 0) * 100) / 100,
      totalContributions: Math.round(items.reduce((sum, item) => sum + item.totalContributions, 0) * 100) / 100
    };
  }

  /**
   * Get payroll label (e.g., "01/2026")
   */
  getPayrollLabel(payroll: Payroll): string {
    return `${String(payroll.month).padStart(2, '0')}/${payroll.year}`;
  }

  /**
   * Check if payroll already exists for a given month/year
   */
  payrollExists(month: number, year: number): Payroll | undefined {
    const payrolls = this.payrollsSubject.getValue();
    return payrolls.find(p => p.month === month && p.year === year);
  }
}
